import React, { useState, useEffect, useMemo } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Stepper,
  Step,
  StepLabel,
  Box,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  CircularProgress,
  Stack,
  TextField,
  Alert,
  Chip,
  Divider,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import PostAddIcon from "@mui/icons-material/PostAdd";
import UserAvatar from "../../../../../common/UserAvatar";
import { useGetTeacherSubjectsQuery } from "../../../../../../app/questionApiSlice";
import {
  useGetClassroomAssessmentsQuery,
  useGetClassroomStudentsQuery,
} from "../../../../../../app/quizApiSlice";
import {
  useGetRecordsBySubjectAndClassQuery,
  useCreateRecordMutation,
  useRevertQuizPostMutation,
} from "../../../../quick-entry/recordApiSlice";

const STEPS = ["Setup", "Review Students", "Confirm"];

const PostToRecordModal = ({ open, onClose, quiz, submissions }) => {
  const [step, setStep] = useState(0);
  const [selectedClassroomId, setSelectedClassroomId] = useState("");
  const [selectedSubjectId, setSelectedSubjectId] = useState("");
  const [selectedAssessmentId, setSelectedAssessmentId] = useState("");
  const [checkedStudents, setCheckedStudents] = useState({});
  const [scoreOverrides, setScoreOverrides] = useState({});
  const [postError, setPostError] = useState(null);
  const [posted, setPosted] = useState(false);
  const [reverting, setReverting] = useState(false);
  const [revertError, setRevertError] = useState(null);
  const [reverted, setReverted] = useState(false);

  const { data: teacherSubjectsData = [] } = useGetTeacherSubjectsQuery();

  const { data: assessmentsData = {} } = useGetClassroomAssessmentsQuery(
    { classroom_id: selectedClassroomId },
    { skip: !selectedClassroomId }
  );

  const { data: classroomStudents = [] } = useGetClassroomStudentsQuery(
    { classroom_id: selectedClassroomId },
    { skip: !selectedClassroomId }
  );

  // Fetch existing records for raw-score merging as soon as classroom+subject are known
  const { data: existingRecordsRaw } = useGetRecordsBySubjectAndClassQuery(
    { subject_id: selectedSubjectId, classroom_id: selectedClassroomId, include_raw: true },
    { skip: !selectedSubjectId || !selectedClassroomId }
  );

  const [createRecord, { isLoading: isPosting }] = useCreateRecordMutation();
  const [revertQuizPost, { isLoading: isReverting }] = useRevertQuizPostMutation();

  // RTK Query may return an array or an object with a key
  const existingRecords = useMemo(() => {
    if (!existingRecordsRaw) return [];
    return Array.isArray(existingRecordsRaw)
      ? existingRecordsRaw
      : existingRecordsRaw.records || [];
  }, [existingRecordsRaw]);

  // Only show the classroom that matches the quiz level
  const classrooms = useMemo(() => {
    const classMap = new Map();
    teacherSubjectsData.forEach((item) => {
      if (
        !classMap.has(item.classroom_id) &&
        item.level?.toLowerCase() === quiz?.level?.toLowerCase()
      ) {
        classMap.set(item.classroom_id, {
          id: item.classroom_id,
          name: item.classroom,
          level: item.level,
        });
      }
    });
    return Array.from(classMap.values());
  }, [teacherSubjectsData, quiz?.level]);

  // Only show the subject that matches the quiz subject name
  const subjects = useMemo(() => {
    if (!selectedClassroomId) return [];
    return teacherSubjectsData
      .filter(
        (item) =>
          item.classroom_id === parseInt(selectedClassroomId) &&
          item.name?.toLowerCase() === quiz?.subject?.toLowerCase()
      )
      .map((item) => ({ id: item.id, name: item.name }));
  }, [teacherSubjectsData, selectedClassroomId, quiz?.subject]);

  const assessments = assessmentsData.assessments || [];
  const selectedAssessment = assessments.find((a) => a.id === parseInt(selectedAssessmentId));
  const totalQ = quiz?.questions?.length || 1;

  // Reset modal state when opened
  useEffect(() => {
    if (!open) return;
    setStep(0);
    setPosted(false);
    setPostError(null);
    setSelectedAssessmentId("");
    setReverting(false);
    setRevertError(null);
    setReverted(false);
  }, [open]);

  // Auto-preselect classroom matching quiz level
  useEffect(() => {
    if (!open || !classrooms.length) return;
    if (selectedClassroomId) return; // already set
    const match = classrooms.find(
      (c) => c.level?.toLowerCase() === quiz?.level?.toLowerCase()
    );
    if (match) setSelectedClassroomId(match.id);
  }, [open, classrooms, quiz?.level]);

  // Auto-preselect subject matching quiz subject
  useEffect(() => {
    if (!selectedClassroomId || !subjects.length) return;
    if (selectedSubjectId) return; // already set
    const match = subjects.find(
      (s) => s.name?.toLowerCase() === quiz?.subject?.toLowerCase()
    );
    if (match) setSelectedSubjectId(match.id);
  }, [selectedClassroomId, subjects, quiz?.subject]);

  // Build student checklist when entering Step 1
  useEffect(() => {
    if (step !== 1) return;
    const newChecked = {};
    const newOverrides = {};

    // Submitters — pre-checked with their correct-answer count
    submissions.forEach((sub) => {
      const correct = sub.correct_count ?? (sub.answers || []).filter((a) => {
        const opt = a.question?.options?.find((o) => o.id === a.option_id);
        return opt?.is_answer === true;
      }).length;
      newChecked[sub.user_id] = true;
      newOverrides[sub.user_id] = correct;
    });

    // Non-submitters — unchecked, default score 0
    const submitterIds = new Set(submissions.map((s) => s.user_id));
    (quiz?.participants || []).forEach((p) => {
      if (!submitterIds.has(p.id)) {
        newChecked[p.id] = false;
        newOverrides[p.id] = 0;
      }
    });

    setCheckedStudents(newChecked);
    setScoreOverrides(newOverrides);
  }, [step]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleClassroomChange = (e) => {
    setSelectedClassroomId(e.target.value);
    setSelectedSubjectId("");
    setSelectedAssessmentId("");
  };

  // All participants for the student review list
  const allParticipants = useMemo(() => {
    const submitterIds = new Set(submissions.map((s) => s.user_id));
    const submitterList = submissions.map((s) => ({
      ...s.user,
      user_id: s.user_id,
      isSubmitter: true,
    }));
    const nonSubmitters = (quiz?.participants || [])
      .filter((p) => !submitterIds.has(p.id))
      .map((p) => ({ ...p, user_id: p.id, isSubmitter: false }));
    return [...submitterList, ...nonSubmitters];
  }, [submissions, quiz?.participants]);

  const selectedCount = Object.values(checkedStudents).filter(Boolean).length;

  const handleSelectAll = (checked) => {
    const newChecked = {};
    allParticipants.forEach((p) => { newChecked[p.user_id] = checked; });
    setCheckedStudents(newChecked);
  };

  const handleConfirm = async () => {
    setPostError(null);

    // Build user_id → student.id map from classroom students
    const studentIdMap = {};
    classroomStudents.forEach((s) => { studentIdMap[s.user_id] = s.id; });

    const records = [];
    Object.entries(checkedStudents).forEach(([userIdStr, isChecked]) => {
      if (!isChecked) return;
      const userId = parseInt(userIdStr);
      const studentId = studentIdMap[userId];
      if (!studentId) return; // student not in this classroom — skip

      const obtained = Math.max(0, Math.min(parseFloat(scoreOverrides[userId]) || 0, totalQ));
      const newEntry = { obtainable: totalQ, obtained, quiz_id: quiz?.id };

      // Append to any existing raw scores for this assessment
      const existing = existingRecords.find(
        (r) => r.student_id === studentId && r.assessment_id === parseInt(selectedAssessmentId)
      );
      const existingScores = existing?.raw?.scores || [];
      const mergedScores = [...existingScores, newEntry];

      records.push({
        assessment_id: parseInt(selectedAssessmentId),
        student_id: studentId,
        raw: { scores: mergedScores },
      });
    });

    if (!records.length) {
      setPostError("No students selected or none could be matched to this classroom.");
      return;
    }

    try {
      await createRecord({
        subject_id: parseInt(selectedSubjectId),
        classroom_id: parseInt(selectedClassroomId),
        records,
      }).unwrap();
      setPosted(true);
    } catch (e) {
      setPostError(e?.data?.message || "Failed to post records. Please try again.");
    }
  };

  const handleRevert = async () => {
    setRevertError(null);
    try {
      await revertQuizPost({
        quiz_id: quiz?.id,
        subject_id: parseInt(selectedSubjectId),
        classroom_id: parseInt(selectedClassroomId),
        assessment_id: parseInt(selectedAssessmentId),
      }).unwrap();
      setReverted(true);
      setPosted(false);
    } catch (e) {
      setRevertError(e?.data?.message || "No posted records found for this quiz.");
    }
  };

  const step0Valid = selectedClassroomId && selectedSubjectId && selectedAssessmentId;
  const step1Valid = selectedCount > 0;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{ sx: { borderRadius: "16px" } }}
    >
      <DialogTitle sx={{ fontWeight: 700, pb: 1 }}>
        <Stack direction="row" alignItems="center" gap={1}>
          <PostAddIcon color="primary" />
          Post Quiz Results to Records
        </Stack>
      </DialogTitle>

      <DialogContent dividers>
        {reverted ? (
          <Box textAlign="center" py={4}>
            <CheckCircleIcon sx={{ fontSize: 56, color: "#2196F3", mb: 2 }} />
            <Typography variant="h6" fontWeight={700}>
              Posting reverted successfully
            </Typography>
            <Typography color="text.secondary" mt={1}>
              Quiz score entries tagged with this quiz have been removed and affected records
              recalculated.
            </Typography>
          </Box>
        ) : posted ? (
          <Box textAlign="center" py={4}>
            <CheckCircleIcon sx={{ fontSize: 56, color: "#27AE60", mb: 2 }} />
            <Typography variant="h6" fontWeight={700}>
              Records posted successfully
            </Typography>
            <Typography color="text.secondary" mt={1}>
              {selectedCount} student record{selectedCount !== 1 ? "s" : ""} updated under{" "}
              <b>{selectedAssessment?.name}</b>.
            </Typography>
            {revertError && (
              <Alert severity="error" sx={{ mt: 2, borderRadius: "10px", textAlign: "left" }}>
                {revertError}
              </Alert>
            )}
          </Box>
        ) : (
          <>
            <Stepper activeStep={step} sx={{ mb: 3 }}>
              {STEPS.map((label) => (
                <Step key={label}>
                  <StepLabel>{label}</StepLabel>
                </Step>
              ))}
            </Stepper>

            {/* ── Step 0: Setup ── */}
            {step === 0 && (
              <Stack gap={2.5}>
                <Typography variant="body2" color="text.secondary">
                  Select the classroom, subject, and assessment to post quiz scores under.
                </Typography>

                <FormControl fullWidth>
                  <InputLabel>Classroom</InputLabel>
                  <Select
                    value={selectedClassroomId}
                    onChange={handleClassroomChange}
                    label="Classroom"
                  >
                    {classrooms.map((c) => (
                      <MenuItem key={c.id} value={c.id}>
                        {c.name}
                        {c.level && (
                          <Chip label={c.level} size="small" sx={{ ml: 1 }} variant="outlined" />
                        )}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <FormControl fullWidth disabled={!selectedClassroomId}>
                  <InputLabel>Subject</InputLabel>
                  <Select
                    value={selectedSubjectId}
                    onChange={(e) => {
                      setSelectedSubjectId(e.target.value);
                      setSelectedAssessmentId("");
                    }}
                    label="Subject"
                  >
                    {subjects.map((s) => (
                      <MenuItem key={s.id} value={s.id}>
                        {s.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <FormControl fullWidth disabled={!selectedSubjectId}>
                  <InputLabel>Assessment</InputLabel>
                  <Select
                    value={selectedAssessmentId}
                    onChange={(e) => setSelectedAssessmentId(e.target.value)}
                    label="Assessment"
                  >
                    {assessments.map((a) => (
                      <MenuItem key={a.id} value={a.id}>
                        {a.name}
                        <Chip
                          label={`/${a.obtainable_score}`}
                          size="small"
                          sx={{ ml: 1 }}
                          variant="outlined"
                        />
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                {selectedAssessment && (
                  <Alert severity="info" sx={{ borderRadius: "10px" }}>
                    Quiz score (<b>{totalQ} questions</b>) will be scaled to{" "}
                    <b>{selectedAssessment.obtainable_score} marks</b> and added as a new
                    sub-score sheet.
                  </Alert>
                )}
              </Stack>
            )}

            {/* ── Step 1: Student Review ── */}
            {step === 1 && (
              <Box>
                <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
                  <Typography variant="body2" color="text.secondary">
                    {selectedCount} of {allParticipants.length} student
                    {allParticipants.length !== 1 ? "s" : ""} selected
                  </Typography>
                  <Box>
                    <Button size="small" onClick={() => handleSelectAll(true)}>
                      Select All
                    </Button>
                    <Button size="small" onClick={() => handleSelectAll(false)}>
                      Deselect All
                    </Button>
                  </Box>
                </Stack>

                <Stack gap={1}>
                  {allParticipants.map((p) => (
                    <Box
                      key={p.user_id}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 2,
                        p: 1.5,
                        borderRadius: "10px",
                        border: "1px solid",
                        borderColor: checkedStudents[p.user_id] ? "primary.main" : "divider",
                        bgcolor: checkedStudents[p.user_id]
                          ? (theme) =>
                              theme.palette.mode === "dark"
                                ? "rgba(39,174,96,0.08)"
                                : "#F0FFF4"
                          : "transparent",
                        transition: "background-color 0.15s, border-color 0.15s",
                      }}
                    >
                      <Checkbox
                        checked={!!checkedStudents[p.user_id]}
                        onChange={(e) =>
                          setCheckedStudents((prev) => ({
                            ...prev,
                            [p.user_id]: e.target.checked,
                          }))
                        }
                      />

                      <UserAvatar
                        src={p.photo || undefined}
                        name={`${p.first_name || ""} ${p.last_name || ""}`}
                        sx={{
                          width: 36,
                          height: 36,
                          fontSize: ".85rem",
                        }}
                      />

                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography sx={{ fontWeight: 600, fontSize: ".9rem" }} noWrap>
                          {p.first_name} {p.last_name}
                        </Typography>
                        <Chip
                          label={p.isSubmitter ? "Submitted" : "No submission"}
                          size="small"
                          color={p.isSubmitter ? "success" : "default"}
                          variant="outlined"
                          sx={{ height: 18, fontSize: ".7rem", mt: 0.25 }}
                        />
                      </Box>

                      <TextField
                        size="small"
                        type="number"
                        label={`Score /${totalQ}`}
                        value={scoreOverrides[p.user_id] ?? ""}
                        onChange={(e) =>
                          setScoreOverrides((prev) => ({
                            ...prev,
                            [p.user_id]: parseFloat(e.target.value) || 0,
                          }))
                        }
                        inputProps={{ min: 0, max: totalQ, step: 1 }}
                        disabled={!checkedStudents[p.user_id]}
                        sx={{ width: 110 }}
                      />
                    </Box>
                  ))}
                </Stack>
              </Box>
            )}

            {/* ── Step 2: Confirm ── */}
            {step === 2 && (
              <Stack gap={2}>
                {postError && (
                  <Alert severity="error" sx={{ borderRadius: "10px" }}>
                    {postError}
                  </Alert>
                )}

                <Typography variant="body2" color="text.secondary">
                  Review the details below then click <b>Record</b>.
                </Typography>

                <Box
                  sx={{
                    p: 2,
                    borderRadius: "10px",
                    bgcolor: (theme) =>
                      theme.palette.mode === "dark" ? "#1e1e1e" : "#F9F9F9",
                    border: "1px solid",
                    borderColor: "divider",
                  }}
                >
                  <Stack gap={1.5}>
                    <Stack direction="row" justifyContent="space-between">
                      <Typography variant="body2" color="text.secondary">
                        Assessment
                      </Typography>
                      <Typography variant="body2" fontWeight={700}>
                        {selectedAssessment?.name}
                      </Typography>
                    </Stack>
                    <Divider />
                    <Stack direction="row" justifyContent="space-between">
                      <Typography variant="body2" color="text.secondary">
                        Obtainable score
                      </Typography>
                      <Typography variant="body2" fontWeight={700}>
                        {selectedAssessment?.obtainable_score} marks
                      </Typography>
                    </Stack>
                    <Divider />
                    <Stack direction="row" justifyContent="space-between">
                      <Typography variant="body2" color="text.secondary">
                        Quiz questions
                      </Typography>
                      <Typography variant="body2" fontWeight={700}>
                        {totalQ}
                      </Typography>
                    </Stack>
                    <Divider />
                    <Stack direction="row" justifyContent="space-between">
                      <Typography variant="body2" color="text.secondary">
                        Students to post
                      </Typography>
                      <Typography variant="body2" fontWeight={700}>
                        {selectedCount}
                      </Typography>
                    </Stack>
                  </Stack>
                </Box>

                <Alert severity="info" sx={{ borderRadius: "10px" }}>
                  Scores are added as a <b>new sub-score sheet</b>. If a student already has
                  entries for this assessment the quiz score is appended and the final mark is
                  recalculated across all sheets.
                </Alert>

                <Typography variant="body2" fontWeight={600}>
                  Selected Students
                </Typography>
                <Stack gap={0.75}>
                  {allParticipants
                    .filter((p) => checkedStudents[p.user_id])
                    .map((p) => (
                      <Stack
                        key={p.user_id}
                        direction="row"
                        justifyContent="space-between"
                        alignItems="center"
                        sx={{
                          px: 2,
                          py: 1,
                          borderRadius: "8px",
                          bgcolor: (theme) =>
                            theme.palette.mode === "dark" ? "#2a2a2a" : "#F5F5F5",
                        }}
                      >
                        <Typography variant="body2">
                          {p.first_name} {p.last_name}
                        </Typography>
                        <Chip
                          label={`${scoreOverrides[p.user_id] ?? 0} / ${totalQ}`}
                          size="small"
                          color={p.isSubmitter ? "success" : "default"}
                          variant="outlined"
                        />
                      </Stack>
                    ))}
                </Stack>
              </Stack>
            )}
          </>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 2, gap: 1 }}>
        {(posted || reverted) ? (
          <>
            {posted && (
              <Button
                variant="outlined"
                color="warning"
                onClick={handleRevert}
                disabled={isReverting}
                startIcon={isReverting ? <CircularProgress size={16} color="inherit" /> : null}
              >
                {isReverting ? "Reverting…" : "Revert"}
              </Button>
            )}
            <Button variant="contained" onClick={onClose}>
              Close
            </Button>
          </>
        ) : (
          <>
            <Button onClick={onClose} disabled={isPosting}>
              Cancel
            </Button>
            {step > 0 && (
              <Button onClick={() => setStep((s) => s - 1)} disabled={isPosting}>
                Back
              </Button>
            )}
            {step < 2 && (
              <Button
                variant="contained"
                onClick={() => setStep((s) => s + 1)}
                disabled={step === 0 ? !step0Valid : !step1Valid}
              >
                Next
              </Button>
            )}
            {step === 2 && (
              <Button
                variant="contained"
                onClick={handleConfirm}
                disabled={isPosting}
                startIcon={isPosting ? <CircularProgress size={16} color="inherit" /> : <PostAddIcon />}
              >
                {isPosting ? "Posting…" : "Record"}
              </Button>
            )}
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default PostToRecordModal;
